import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import MyNavbar from "../navbar";
import Footer from "../footer";
import Scrolltotopbtn from "../Scrolltotopbutton";
import Notification from "../Notification";
import { useProductOptions } from "../../useProductOptions";

// A step-by-step alternative to the single long add-product form. It posts the
// same FormData to the same /addproducts endpoint, so nothing downstream knows
// the difference -- the existing form is untouched and still routed.
//
// The design goal is that a seller is never asked for more than one kind of
// thing at a time, always knows how far through they are, and cannot reach the
// end with a listing the server will reject.

// Mirrors the limits the existing form and the /addproducts handler enforce.
const MIN_IMAGES = 2;
const MAX_IMAGES = 6;
const MIN_PRICE = 2;

const STEPS = [
  { id: "what", title: "What are you selling?", hint: "Pick the department and category" },
  { id: "photos", title: "Photos", hint: "Two to six pictures, and an optional video" },
  { id: "details", title: "Details", hint: "The things buyers ask about" },
  { id: "price", title: "Price", hint: "What it costs and how many you have" },
  { id: "review", title: "Review", hint: "Check it over, then publish" },
];

const SIZES_BY_TYPE = {
  kids: ["NA", "0-2 Years", "2-4 Years", "4-6 Years", "6-8 Years", "8-10 Years", "10-15 Years"],
  women: ["NA", "XS", "S", "M", "L", "XL"],
  men: ["NA", "XS", "S", "M", "L", "XL"],
};

// Defined at module scope on purpose. A component declared inside the wizard's
// render is a brand new type on every keystroke, so React unmounts the input it
// wraps and the field loses focus after a single character. Shared state
// reaches them through context instead of props, so call sites stay readable.
const WizardContext = React.createContext({ values: {}, errors: {}, set: () => {} });

const Field = ({ label, name, children, optional }) => {
  const { errors } = React.useContext(WizardContext);
  return (
    <div className="mb-3">
      <label className="form-label mb-1" htmlFor={name}>
        {label}
        {optional && <span className="text-secondary"> (optional)</span>}
      </label>
      {children}
      {errors[name] && (
        <div className="text-danger" style={{ fontSize: "13px" }}>
          {errors[name]}
        </div>
      )}
    </div>
  );
};

const Select = ({ name, options, placeholder }) => {
  const { values, set } = React.useContext(WizardContext);
  return (
    <select
      id={name}
      className="form-select"
      value={values[name]}
      onChange={(e) => set(name, e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const value = typeof o === "string" ? o : o.value;
        const key = typeof o === "string" ? o : o.id;
        return (
          <option key={key} value={value}>
            {value}
          </option>
        );
      })}
    </select>
  );
};

const AddProductWizard = () => {
  const navigate = useNavigate();
  const productOptions = useProductOptions();
  const sellerId = localStorage.getItem("user-token") || "";

  const [step, setStep] = useState(0);
  const [productTypes, setProductTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]); // File[]
  const [video, setVideo] = useState(null);
  const [previews, setPreviews] = useState([]); // object urls
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const fileInputRef = useRef(null);

  const [values, setValues] = useState({
    producttype: "",
    category: "",
    productname: "",
    productdescription: "",
    location: "",
    color: "",
    alteration: "",
    size: "",
    measurements: "",
    material: "",
    condition: "",
    source: "",
    age: "",
    language: "",
    quantity: "1",
    price: "",
    currency: "USD",
    notes: "",
    accepted_by_admin: "false",
    seller_id: sellerId,
  });

  const notify = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const set = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  // --- option data ----------------------------------------------------------
  useEffect(() => {
    fetch(`${process.env.REACT_APP_HOST}${process.env.REACT_APP_PORT}/api/getProductTypes`)
      .then((r) => r.json())
      .then((d) => Array.isArray(d) && setProductTypes(d.map((i) => i.producttype)))
      .catch((err) => console.error("Could not load product types:", err.message));
  }, []);

  useEffect(() => {
    if (!values.producttype) return;
    fetch(
      `${process.env.REACT_APP_HOST}${process.env.REACT_APP_PORT}/api/getCategories/${values.producttype}`
    )
      .then((r) => r.json())
      .then((d) => Array.isArray(d) && setCategories(d.map((i) => i.category)))
      .catch((err) => console.error("Could not load categories:", err.message));
  }, [values.producttype]);

  const isJewellery = values.producttype === "jewellery";
  const sizes = SIZES_BY_TYPE[values.producttype] || ["NA", "XS", "S", "M", "L", "XL"];

  // --- images ---------------------------------------------------------------
  // Object urls are revoked when the set changes, so a long session does not
  // leak a preview for every photo the seller tried.
  useEffect(() => {
    const urls = images.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [images]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const pics = incoming.filter((f) => f.type.startsWith("image/"));
    const vid = incoming.find((f) => f.type.startsWith("video/"));

    if (vid) setVideo(vid);
    if (pics.length) {
      setImages((current) => {
        const next = [...current, ...pics].slice(0, MAX_IMAGES);
        if (current.length + pics.length > MAX_IMAGES) {
          notify(`Only the first ${MAX_IMAGES} photos were kept.`, "error");
        }
        return next;
      });
      setErrors((e) => ({ ...e, images: undefined }));
    }
  };

  const removeImage = (i) => setImages((c) => c.filter((_, idx) => idx !== i));
  const moveImage = (i, dir) =>
    setImages((c) => {
      const next = [...c];
      const j = i + dir;
      if (j < 0 || j >= next.length) return c;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  // --- per-step validation --------------------------------------------------
  const validateStep = useCallback(
    (index) => {
      const e = {};
      if (index === 0) {
        if (!values.producttype) e.producttype = "Choose a department.";
        if (!values.category) e.category = "Choose a category.";
      }
      if (index === 1) {
        if (images.length < MIN_IMAGES) e.images = `Add at least ${MIN_IMAGES} photos.`;
        if (images.length > MAX_IMAGES) e.images = `No more than ${MAX_IMAGES} photos.`;
      }
      if (index === 2) {
        if (!values.productname.trim()) e.productname = "Give the item a name.";
        if (!values.productdescription.trim()) e.productdescription = "Describe the item.";
        if (!values.color.trim()) e.color = "What colour is it?";
        if (!values.condition) e.condition = "Pick a condition.";
      }
      if (index === 3) {
        const price = parseFloat(values.price);
        if (!values.price) e.price = "Set a price.";
        else if (!Number.isFinite(price)) e.price = "That is not a number.";
        else if (price < MIN_PRICE) e.price = `The lowest we can list is $${MIN_PRICE}.`;
        const qty = parseInt(values.quantity, 10);
        if (!Number.isInteger(qty) || qty < 1) e.quantity = "At least one.";
      }
      return e;
    },
    [values, images]
  );

  const next = () => {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const furthestValid = useMemo(() => {
    for (let i = 0; i < STEPS.length; i++) {
      if (Object.keys(validateStep(i)).length) return i;
    }
    return STEPS.length - 1;
  }, [validateStep]);

  // --- submit ---------------------------------------------------------------
  const publish = async () => {
    // Re-check everything: a seller can jump back and edit after reaching review.
    for (let i = 0; i < STEPS.length - 1; i++) {
      const e = validateStep(i);
      if (Object.keys(e).length) {
        setErrors(e);
        setStep(i);
        notify("Something needs fixing on this step.", "error");
        return;
      }
    }

    const payload = { ...values };
    // The existing form applies these defaults; keep the data shape identical.
    if (payload.producttype === "jewellery") {
      payload.size = "NA";
      payload.material = "NA";
      payload.source = "NA";
    }
    if (payload.producttype === "women") payload.alteration = payload.alteration || "NA";
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") payload[k] = "NA";
    });
    payload.seller_id = sellerId;
    payload.accepted_by_admin = "false";

    const formData = new FormData();
    images.forEach((img) => formData.append("media", img));
    if (video) formData.append("media", video);
    Object.keys(payload).forEach((k) => formData.append(k, payload[k]));

    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_HOST}${process.env.REACT_APP_PORT}/addproducts`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      notify("Listing submitted. An admin will review it shortly.", "success");
      setTimeout(() => navigate("/sellerproducts"), 1500);
    } catch (err) {
      console.error("Could not publish listing:", err);
      notify(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Could not publish the listing. Please try again.",
        "error"
      );
      setSubmitting(false);
    }
  };

  return (
    <WizardContext.Provider value={{ values, errors, set }}>
    <div>
      <MyNavbar />
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="container py-4" style={{ maxWidth: "860px" }}>
        <h4 className="mb-1">List an item</h4>
        <p className="text-secondary mb-4" style={{ fontSize: "14px" }}>
          Step {step + 1} of {STEPS.length} &middot; {STEPS[step].hint}
        </p>
        <p className="mb-4" style={{ fontSize: "13px" }}>
          <Link to="/addnewproduct" className="text-secondary">
            Prefer one long form? Use the classic page.
          </Link>
        </p>

        {/* progress rail -- each step is clickable once it is reachable */}
        <div className="d-flex gap-2 mb-4 flex-wrap">
          {STEPS.map((s, i) => {
            const done = i < step;
            const current = i === step;
            const reachable = i <= furthestValid + 1;
            return (
              <button
                key={s.id}
                type="button"
                className={`btn btn-sm ${
                  current ? "btn-dark" : done ? "btn-outline-dark" : "btn-outline-secondary"
                }`}
                style={{ opacity: reachable ? 1 : 0.5 }}
                disabled={!reachable}
                onClick={() => reachable && setStep(i)}
              >
                {done ? "✓ " : `${i + 1}. `}
                {s.title}
              </button>
            );
          })}
        </div>

        <div className="border rounded p-4">
          {/* ---------------------------------------------------------- 1 */}
          {step === 0 && (
            <>
              <Field label="Department" name="producttype">
                <div className="d-flex flex-wrap gap-2">
                  {productTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`btn ${
                        values.producttype === t ? "btn-dark" : "btn-outline-dark"
                      }`}
                      onClick={() => {
                        set("producttype", t);
                        set("category", "");
                      }}
                    >
                      {t}
                    </button>
                  ))}
                  {productTypes.length === 0 && (
                    <span className="text-secondary">Loading departments…</span>
                  )}
                </div>
              </Field>

              {values.producttype && (
                <Field label="Category" name="category">
                  <div className="d-flex flex-wrap gap-2">
                    {categories.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`btn btn-sm ${
                          values.category === c ? "btn-dark" : "btn-outline-secondary"
                        }`}
                        onClick={() => set("category", c)}
                      >
                        {c}
                      </button>
                    ))}
                    {categories.length === 0 && (
                      <span className="text-secondary">No categories for this department yet.</span>
                    )}
                  </div>
                </Field>
              )}
            </>
          )}

          {/* ---------------------------------------------------------- 2 */}
          {step === 1 && (
            <>
              <div
                className="border rounded p-4 text-center mb-3"
                style={{ borderStyle: "dashed", cursor: "pointer" }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  addFiles(e.dataTransfer.files);
                }}
              >
                <div style={{ fontSize: "15px" }}>Drop photos here, or click to choose</div>
                <div className="text-secondary" style={{ fontSize: "13px" }}>
                  {MIN_IMAGES}–{MAX_IMAGES} photos. A short video is optional.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/mp4,video/quicktime"
                  multiple
                  hidden
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </div>

              {errors.images && (
                <div className="text-danger mb-2" style={{ fontSize: "13px" }}>
                  {errors.images}
                </div>
              )}

              <div className="d-flex flex-wrap gap-2">
                {previews.map((src, i) => (
                  <div key={src} className="border rounded p-1" style={{ width: "120px" }}>
                    <img
                      src={src}
                      alt=""
                      style={{ width: "100%", height: "90px", objectFit: "cover" }}
                    />
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <span className="text-secondary" style={{ fontSize: "11px" }}>
                        {i === 0 ? "cover" : i + 1}
                      </span>
                      <span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 me-1"
                          onClick={() => moveImage(i, -1)}
                          disabled={i === 0}
                          title="Move earlier"
                        >
                          ←
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 me-1"
                          onClick={() => moveImage(i, 1)}
                          disabled={i === previews.length - 1}
                          title="Move later"
                        >
                          →
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={() => removeImage(i)}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {video && (
                <div className="mt-3 d-flex align-items-center gap-2">
                  <span style={{ fontSize: "14px" }}>Video: {video.name}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={() => setVideo(null)}
                  >
                    remove
                  </button>
                </div>
              )}

              <p className="text-secondary mt-3 mb-0" style={{ fontSize: "13px" }}>
                The first photo is the one buyers see in listings. Use the arrows to reorder.
              </p>
            </>
          )}

          {/* ---------------------------------------------------------- 3 */}
          {step === 2 && (
            <>
              <Field label="Item name" name="productname">
                <input
                  id="productname"
                  className="form-control"
                  value={values.productname}
                  maxLength={120}
                  onChange={(e) => set("productname", e.target.value)}
                  placeholder="e.g. Soft Silk Saree with Gold Border"
                />
              </Field>

              <Field label="Description" name="productdescription">
                <textarea
                  id="productdescription"
                  className="form-control"
                  rows={4}
                  value={values.productdescription}
                  onChange={(e) => set("productdescription", e.target.value)}
                  placeholder="Fabric, fit, how often it was worn, any flaws worth mentioning."
                />
              </Field>

              <div className="row">
                <div className="col-md-6">
                  <Field label="Colour" name="color">
                    <input
                      id="color"
                      className="form-control"
                      value={values.color}
                      onChange={(e) => set("color", e.target.value)}
                      placeholder="e.g. Deep maroon"
                    />
                  </Field>
                </div>
                <div className="col-md-6">
                  <Field label="Condition" name="condition">
                    <Select
                      name="condition"
                      options={productOptions.condition}
                      placeholder="Select condition"
                    />
                  </Field>
                </div>
                {!isJewellery && (
                <div className="col-md-6">
                  <Field label="Material" name="material" optional>
                    <Select
                      name="material"
                      options={productOptions.material}
                      placeholder="Select material"
                    />
                  </Field>
                </div>
                )}
                <div className="col-md-6">
                  <Field label="Style" name="age" optional>
                    <Select name="age" options={productOptions.style} placeholder="Select style" />
                  </Field>
                </div>
                {!isJewellery && (
                <div className="col-md-6">
                  <Field label="Size" name="size" optional>
                    <Select name="size" options={sizes} placeholder="Select size" />
                  </Field>
                </div>
                )}
                <div className="col-md-6">
                  <Field label="Measurements" name="measurements" optional>
                    <input
                      id="measurements"
                      className="form-control"
                      value={values.measurements}
                      onChange={(e) => set("measurements", e.target.value)}
                      placeholder="e.g. Length 5.5m, blouse 0.8m"
                    />
                  </Field>
                </div>
                <div className="col-md-6">
                  <Field label="Alterations" name="alteration" optional>
                    <input
                      id="alteration"
                      className="form-control"
                      value={values.alteration}
                      onChange={(e) => set("alteration", e.target.value)}
                      placeholder="Any alterations made"
                    />
                  </Field>
                </div>
                <div className="col-md-6">
                  <Field label="Location" name="location" optional>
                    <input
                      id="location"
                      className="form-control"
                      value={values.location}
                      onChange={(e) => set("location", e.target.value)}
                      placeholder="Where it ships from"
                    />
                  </Field>
                </div>
              </div>
            </>
          )}

          {/* ---------------------------------------------------------- 4 */}
          {step === 3 && (
            <div className="row">
              <div className="col-md-6">
                <Field label="Price (USD)" name="price">
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      id="price"
                      type="number"
                      className="form-control"
                      min={MIN_PRICE}
                      step="0.01"
                      value={values.price}
                      onChange={(e) => set("price", e.target.value)}
                      placeholder="18.41"
                    />
                  </div>
                </Field>
                <p className="text-secondary" style={{ fontSize: "13px" }}>
                  Minimum ${MIN_PRICE}. Shipping is on us for US orders.
                </p>
              </div>
              <div className="col-md-6">
                <Field label="How many do you have?" name="quantity">
                  <input
                    id="quantity"
                    type="number"
                    className="form-control"
                    min="1"
                    value={values.quantity}
                    onChange={(e) => set("quantity", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Field label="Private notes for the admin" name="notes" optional>
                  <input
                    id="notes"
                    className="form-control"
                    value={values.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Anything the reviewer should know. Buyers never see this."
                  />
                </Field>
              </div>
            </div>
          )}

          {/* ---------------------------------------------------------- 5 */}
          {step === 4 && (
            <>
              <div className="d-flex gap-3 flex-wrap">
                {previews[0] && (
                  <img
                    src={previews[0]}
                    alt=""
                    style={{ width: "160px", height: "160px", objectFit: "cover" }}
                    className="rounded border"
                  />
                )}
                <div className="flex-grow-1">
                  <h5 className="mb-1">{values.productname || "Untitled item"}</h5>
                  <div className="fw-bold mb-2">
                    $ {parseFloat(values.price || 0).toFixed(2)}
                  </div>
                  <div className="text-secondary" style={{ fontSize: "14px" }}>
                    {values.producttype} / {values.category}
                  </div>
                  <div className="text-secondary" style={{ fontSize: "14px" }}>
                    {[values.condition, values.material, values.size]
                      .filter((v) => v && v !== "NA")
                      .join(" · ")}
                  </div>
                  <div className="text-secondary mt-1" style={{ fontSize: "13px" }}>
                    {images.length} photo{images.length === 1 ? "" : "s"}
                    {video ? " + video" : ""} · quantity {values.quantity}
                  </div>
                </div>
              </div>

              <p className="mt-3 mb-0" style={{ fontSize: "14px" }}>
                {values.productdescription}
              </p>

              <p className="text-secondary mt-3 mb-0" style={{ fontSize: "13px" }}>
                Listings go to an admin for review before they appear in the shop.
              </p>
            </>
          )}
        </div>

        {/* navigation */}
        <div className="d-flex justify-content-between mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={back}
            disabled={step === 0 || submitting}
          >
            Back
          </button>

          {step < STEPS.length - 1 ? (
            <button type="button" className="btn btn-dark px-4" onClick={next}>
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-dark px-4"
              onClick={publish}
              disabled={submitting}
            >
              {submitting ? "Publishing…" : "Publish listing"}
            </button>
          )}
        </div>
      </div>

      <Scrolltotopbtn />
      <Footer />
    </div>
    </WizardContext.Provider>
  );
};

export default AddProductWizard;
